"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import gql from "graphql-tag";
import type {
  CancelCanvasPhoneAppointmentInput,
  CanvasSchedulerResponse,
} from "@rmh/defs";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

interface CancelCanvasPhoneAppointmentVariables {
  input: CancelCanvasPhoneAppointmentInput;
}

interface CancelCanvasPhoneAppointmentResponse {
  data?: {
    cancelCanvasPhoneAppointment: CanvasSchedulerResponse;
  };
}

export const cancelCanvasPhoneAppointmentMutation = gql`
  mutation cancelCanvasPhoneAppointment(
    $input: CancelCanvasPhoneAppointmentInput!
  ) {
    cancelCanvasPhoneAppointment(input: $input) {
      success
      error {
        type
        message
        details
      }
      data {
        appointmentId
        canvasAppointmentId
        canvasPractitionerId
        appointmentDateTime
        timestamp
      }
    }
  }
`.loc?.source.body as GeneratedMutation<
  CancelCanvasPhoneAppointmentVariables,
  {
    cancelCanvasPhoneAppointment: CanvasSchedulerResponse;
  }
>;

export const useCancelCanvasPhoneAppointment = (
  options: MutationOptions<
    CancelCanvasPhoneAppointmentResponse,
    Error,
    CancelCanvasPhoneAppointmentVariables
  > = {}
) => {
  const { mutateAsync, isPending } = useMutation<
    CancelCanvasPhoneAppointmentResponse,
    Error,
    CancelCanvasPhoneAppointmentVariables
  >({
    ...options,
    mutationFn: async (variables) =>
      fetchGraphQLClient(cancelCanvasPhoneAppointmentMutation, variables),
  });

  const cancelCanvasPhoneAppointment = async (
    input: CancelCanvasPhoneAppointmentInput
  ) => {
    try {
      const response = await mutateAsync({ input });

      return response?.data?.cancelCanvasPhoneAppointment;
    } catch (error) {
      console.error("Failed to cancel Canvas phone appointment", error);
      throw error;
    }
  };

  return {
    cancelCanvasPhoneAppointment,
    isPending,
  };
};
